import { Controller } from 'stimulus'

// Shared column widths on the job samples grid and the task results grid
// (see app/models/grid_column_widths.rb). Unlike the jobs index table, the
// widths belong to the job / task, so everyone sees the same grid.
//
// Lives on the grid's <table>. Every resizable th/td carries data-col="<key>",
// and the widths are CSS rules in the table's inline <style>: rendered
// server-side for first paint, rewritten here while a column is dragged, so
// rows added later by infinite scroll or a broadcast match with no per-row
// work. The frozen columns are pinned by freeze-columns transforms, which do
// not depend on widths, so nothing needs re-measuring after a resize.
//
// Persistence: PATCH the one column that changed (key + width, a blank width
// resets it). The server merges it into what is stored, so two people sizing
// different columns at once both keep theirs.
const MIN_WIDTH = 48
const MAX_WIDTH = 1200
const KEY_FORMAT = /^[a-z][a-z0-9_]{0,40}$/

export default class extends Controller {
  static targets = ['style'];

  connect() {
    this.widths = this.parseJson(this.data.get('widths'))
    this.url = this.data.get('url')
    this.cssScope = this.data.get('scope') // not `scope`: Stimulus owns that name
  }

  disconnect() {
    if (this._finishResize) this._finishResize()
  }

  // Pointer events cover mouse, touch, and pen; capture keeps the drag alive
  // when the pointer leaves the 8px handle.
  startResize(event) {
    if (event.button !== undefined && event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()

    const handle = event.currentTarget
    const th = handle.closest('th')
    const key = th && th.dataset.col
    if (!key) return

    const startX = event.clientX
    const startWidth = th.getBoundingClientRect().width
    let moved = false

    this.element.classList.add('is-resizing')
    th.classList.add('is-resizing')

    const move = (ev) => {
      const width = Math.round(startWidth + (ev.clientX - startX))
      this.widths[key] = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width))
      moved = true
      this.scheduleRender()
    }
    const finish = () => {
      this._finishResize = null
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', finish)
      handle.removeEventListener('pointercancel', finish)
      if (handle.hasPointerCapture && handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId)
      }
      this.element.classList.remove('is-resizing')
      th.classList.remove('is-resizing')
      this.render()
      if (moved) this.persist(key, this.widths[key])
    }
    this._finishResize = finish

    if (handle.setPointerCapture) handle.setPointerCapture(event.pointerId)
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', finish)
    handle.addEventListener('pointercancel', finish)
  }

  // Double-click on a handle: back to the grid's automatic width.
  resetWidth(event) {
    event.preventDefault()
    event.stopPropagation()
    const th = event.currentTarget.closest('th')
    const key = th && th.dataset.col
    if (!key || !(key in this.widths)) return
    delete this.widths[key]
    this.render()
    this.persist(key, '')
  }

  // Keep a click that ends a drag from reaching anything above the handle.
  swallow(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  scheduleRender() {
    if (this._raf) return
    this._raf = requestAnimationFrame(() => {
      this._raf = null
      this.render()
    })
  }

  // Mirrors GridColumnWidths.css.
  render() {
    if (!this.hasStyleTarget || !this.cssScope) return
    this.styleTarget.textContent = Object.keys(this.widths)
      .filter(key => KEY_FORMAT.test(key))
      .map((key) => {
        const px = this.widths[key]
        return `${this.cssScope} th[data-col="${key}"],${this.cssScope} td[data-col="${key}"]`
          + `{width:${px}px;min-width:${px}px;max-width:${px}px}`
      })
      .join('\n')
  }

  persist(key, width) {
    if (!this.url) return
    const formData = new FormData()
    formData.append('key', key)
    formData.append('width', width)

    // The width already applies on this page, so only say so when it did
    // not save: it would otherwise quietly revert on the next load.
    const notSaved = () => {
      if (window.toastr) window.toastr.error('The column width could not be saved.')
    }
    fetch(this.url, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
      body: formData,
      credentials: 'same-origin'
    }).then((response) => { if (!response.ok) notSaved() }).catch(notSaved)
  }

  parseJson(raw) {
    if (!raw) return {}
    try {
      const value = JSON.parse(raw)
      return value && typeof value === 'object' ? value : {}
    } catch (_) {
      return {}
    }
  }
}
